// src/pages/partners/PartnerDetail.tsx
//
// PartnerDetail
// - Admin-facing partner detail page.
// - Allows editing of partner portal credentials and API credentials, and applying deposits.
// - Tracks section-level "dirty" state to highlight unsaved changes.
// - Uses shared Confirm and SystemAlert for all user interactions (no window.alert/confirm).
//
// Data flow (source of truth)
// - Partner profile is hydrated from `usePartnerDetail(partnerId)`.
// - Save: `updatePartner(partnerId, form)` then `refetchPartnerDetail()` to refresh server-sourced values.
// - Deposit: `updateDeposit(partnerId, amount)` is applied immediately; transactions are refreshed afterwards.
//
// PageHeader integration
// - Leading: partner name and meta line (Partner ID / Manual Order).
// - Actions: Reset / Save (enabled only when there are unsaved changes).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Replay, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { Buffer } from 'buffer';

import { updateDeposit, updatePartner } from '@apis/partner';
import { usePartnerDetail } from '@queries/partners/usePartnerDetail';
import { usePartnerTransactions } from '@queries/partners/usePartnerTransactions';
import { usePartnerTransactionCount } from '@queries/partners/usePartnerTransactionCount';

import type { Partner, PartnerForm, PartnerTransaction } from '@models/partner';
import type { PartnerTransactionRow } from '@components/partner/RecentTransactionsTable';

import { toUsdDisplay } from '@utils/money';
import { toDisplayPartnerId } from './util';

import AccountInformationCard from '@components/partner/AccountInformationCard';
import ApiSettingsCard from '@components/partner/ApiSettingsCard';
import ProductAccessPricingCard from '@components/partner/ProductAccessPricingCard';
import BalanceCard from '@components/partner/BalanceCard';
import RecentTransactionsTable from '@components/partner/RecentTransactionsTable';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

const PAGE_SIZE = 50;

const EMPTY_PARTNER: Partner = {
  name: '',
  partnerId: 0,
  managerUsername: '',
  managerPassword: '',
  managerEmail: '',
  managerName: '',
  managerTelephone: '',
  address: '',
  balance: 0,
  baseDiscountRate: 0,
  callbackUrl: '',
  remoteIps: [],
  appId: '',
  appSecret: '',
  createdAt: '',
  manualOrderEnabled: false,
};

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

/**
 * Section wrapper used by child cards to keep layout consistent across the page.
 * When `highlighted` is true, the card shows a subtle warning outline to indicate
 * there are unsaved changes in that section.
 */
const SectionCard = ({
  title,
  icon,
  right,
  description,
  highlighted,
  children,
}: SectionWrapperArgs) => {
  return (
    <Paper
      sx={(theme) => {
        const accent = theme.palette.warning.main;
        const outlineColor = alpha(
          accent,
          theme.palette.mode === 'dark' ? 0.55 : 0.75,
        );

        return {
          position: 'relative',
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          transition: 'box-shadow 200ms ease, border-color 200ms ease',
          boxShadow: highlighted ? `0 0 0 2px ${outlineColor}` : 'none',
        };
      }}
    >
      {/* Left accent bar (kept mounted so fade-out animation works). */}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          backgroundColor: theme.palette.warning.main,
          opacity: highlighted ? 1 : 0,
          transition: 'opacity 200ms ease',
          pointerEvents: 'none',
        })}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        mb={description ? 0.75 : 1.5}
        sx={{ minWidth: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {icon}
          <Typography variant="h6" fontWeight={700} sx={{ minWidth: 0 }}>
            {title}
          </Typography>
        </Stack>

        {right ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{right}</Box>
        ) : null}
      </Stack>

      {description ? (
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {description}
        </Typography>
      ) : null}

      {children}
    </Paper>
  );
};

/**
 * Generates a base64-encoded UUID string.
 * Note: This is a UI convenience. The final credential validation/acceptance
 * is enforced by the backend.
 */
const makeBase64Uuid = () => Buffer.from(uuid()).toString('base64');

/** Generates a short password token for portal credential rotation. */
const makeShortPassword = () => makeBase64Uuid().slice(0, 8);

const PartnerDetail = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const safePartnerId = useMemo(() => {
    const n = Number(partnerId);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [partnerId]);

  /**
   * The page edits a local form, while the API response remains the source of truth.
   * - `form`: current editable values
   * - `original`: last server-synced baseline for dirty checks and reset
   */
  const [form, setForm] = useState<Partner>(EMPTY_PARTNER);
  const [original, setOriginal] = useState<Partner>(EMPTY_PARTNER);

  /** Save state is kept separate from react-query loading flags for UI control. */
  const [saveLoading, setSaveLoading] = useState(false);

  /** Deposit is applied immediately (not part of the Save flow). */
  const [deposit, setDeposit] = useState<number>(0);
  const [depositApplying, setDepositApplying] = useState(false);

  /** Transactions pagination */
  const [page, setPage] = useState(1);
  const txAnchorRef = useRef<HTMLDivElement | null>(null);

  /* ------------------------------------------------------------------ */
  /* Queries                                                             */
  /* ------------------------------------------------------------------ */

  const { data: partnerDetailRes, refetch: refetchPartnerDetail } =
    usePartnerDetail(safePartnerId);

  const {
    data: partnerTransactionsRes,
    refetch: refetchTransactions,
    isLoading: isTxLoading,
  } = usePartnerTransactions({
    partnerId: safePartnerId,
    page,
    pageSize: PAGE_SIZE,
  });

  const { data: txCount, refetch: refetchTxCount } =
    usePartnerTransactionCount(safePartnerId);

  /* ------------------------------------------------------------------ */
  /* Hydration / partner switch handling                                 */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const loaded = partnerDetailRes?.data?.[0];
    if (!loaded) return;

    setForm(loaded);
    setOriginal(loaded);
  }, [partnerDetailRes]);

  // Reset local paging and deposit input when switching partners.
  useEffect(() => {
    setPage(1);
    setDeposit(0);
  }, [safePartnerId]);

  /* ------------------------------------------------------------------ */
  /* Input adapter                                                       */
  /* ------------------------------------------------------------------ */

  const handleChangeInput = useCallback(
    (name: keyof PartnerForm, value: string | boolean | number | null) => {
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  /* ------------------------------------------------------------------ */
  /* Dirty tracking (section level)                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Section-level dirty flags drive card highlighting and action enablement.
   * Balance is intentionally excluded because deposits are applied immediately.
   */
  const isAccountDirty = useMemo(() => {
    return (
      form.managerPassword !== original.managerPassword ||
      form.managerName !== original.managerName ||
      form.managerEmail !== original.managerEmail ||
      form.managerTelephone !== original.managerTelephone ||
      form.address !== original.address ||
      Boolean(form.manualOrderEnabled) !== Boolean(original.manualOrderEnabled)
    );
  }, [form, original]);

  const isApiDirty = useMemo(() => {
    return (
      form.appId !== original.appId ||
      form.appSecret !== original.appSecret ||
      form.callbackUrl !== original.callbackUrl
    );
  }, [form, original]);

  const isDirty = useMemo(
    () => isAccountDirty || isApiDirty,
    [isAccountDirty, isApiDirty],
  );

  /* ------------------------------------------------------------------ */
  /* Transactions mapping (API -> table rows)                            */
  /* ------------------------------------------------------------------ */

  /**
   * Normalizes the backend transaction payload into the table row shape expected
   * by `RecentTransactionsTable`.
   *
   * Note: Guardrails are intentionally included because backend payloads may
   * contain optional or missing fields.
   */
  const transactions = useMemo<PartnerTransactionRow[]>(() => {
    const raw = (partnerTransactionsRes?.data ?? []) as PartnerTransaction[];

    const safeString = (v: unknown, fallback = '-') => {
      if (typeof v !== 'string') return fallback;
      const t = v.trim();
      return t ? t : fallback;
    };

    const safeNumber = (v: unknown, fallback = 0) => {
      const num = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(num) ? num : fallback;
    };

    return raw.map((t) => ({
      partnerTransactionId: (t as any).partnerTransactionId,
      type: safeString((t as any).type, 'UNKNOWN'),

      partnerTransactionCode: safeString((t as any).partnerTransactionCode, '-'),
      orderCode: safeString((t as any).orderCode, '-'),
      productCode: safeString((t as any).productCode, '-'),
      productName: safeString((t as any).productName, '-'),

      quantity: safeNumber((t as any).quantity, 0),
      previousBalance: safeNumber((t as any).previousBalance, 0),
      addedAmount: safeNumber((t as any).addedAmount, 0),

      balance:
        typeof (t as any).balance === 'number' ? (t as any).balance : undefined,
      createdAt: (t as any).createdAt,
    }));
  }, [partnerTransactionsRes]);

  /* ------------------------------------------------------------------ */
  /* UI helpers                                                          */
  /* ------------------------------------------------------------------ */

  const scrollToTransactions = useCallback(() => {
    const el = txAnchorRef.current;
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  /**
   * Restores editable fields to the last saved baseline.
   * Deposits are excluded because they are applied immediately on the server.
   */
  const resetEditableSections = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      // Account section
      managerPassword: original.managerPassword,
      managerName: original.managerName,
      managerEmail: original.managerEmail,
      managerTelephone: original.managerTelephone,
      address: original.address,
      manualOrderEnabled: original.manualOrderEnabled,
      // API section
      appId: original.appId,
      appSecret: original.appSecret,
      callbackUrl: original.callbackUrl,
    }));
  }, [original]);

  /* ------------------------------------------------------------------ */
  /* Save / Deposit flows                                                */
  /* ------------------------------------------------------------------ */

  const handleSave = useCallback(async () => {
    if (!safePartnerId || !isDirty || saveLoading) return;

    try {
      setSaveLoading(true);
      await updatePartner(safePartnerId, form);

      // Refetch to refresh server-computed fields and reset the dirty baseline.
      await refetchPartnerDetail();
      systemAlert.success('Changes saved successfully.');
    } catch {
      systemAlert.error('Failed to save changes. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  }, [
    form,
    isDirty,
    refetchPartnerDetail,
    safePartnerId,
    saveLoading,
    systemAlert,
  ]);

  /**
   * Applies a deposit immediately.
   * This action is not part of the Save flow and is treated as irreversible in normal operations.
   */
  const applyDeposit = useCallback(async () => {
    if (!safePartnerId) return;
    if (deposit <= 0 || depositApplying) return;

    try {
      setDepositApplying(true);

      await updateDeposit(safePartnerId, deposit);

      // Keep the page responsive by updating the displayed balance immediately.
      setForm((prev) => ({ ...prev, balance: prev.balance + deposit }));

      systemAlert.success(`Deposit applied: ${toUsdDisplay(deposit)}`);
      setDeposit(0);

      // Best-effort refresh so the transaction table reflects the new deposit entry.
      try {
        await Promise.all([refetchTransactions(), refetchTxCount()]);
      } catch {
        systemAlert.info('Deposit applied, but failed to refresh transactions.');
      }

      scrollToTransactions();
    } catch {
      systemAlert.error('Failed to apply deposit. Please try again.');
    } finally {
      setDepositApplying(false);
    }
  }, [
    deposit,
    depositApplying,
    refetchTransactions,
    refetchTxCount,
    safePartnerId,
    scrollToTransactions,
    systemAlert,
  ]);

  /* ------------------------------------------------------------------ */
  /* Credential rotation helpers (UI convenience)                         */
  /* ------------------------------------------------------------------ */

  const regeneratePassword = useCallback(() => {
    setForm((prev) => ({ ...prev, managerPassword: makeShortPassword() }));
  }, []);

  const regenerateAppId = useCallback(() => {
    setForm((prev) => ({ ...prev, appId: makeBase64Uuid() }));
  }, []);

  const regenerateAppSecret = useCallback(() => {
    setForm((prev) => ({ ...prev, appSecret: makeBase64Uuid() }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Confirm flows (shared confirm modal + shared toasts)                */
  /* ------------------------------------------------------------------ */

  const confirmReset = useCallback(async () => {
    const ok = await confirm({
      priority: 'warning',
      title: 'Reset changes?',
      description: (
        <>
          This will discard all unsaved changes in <b>Account Information</b> and{' '}
          <b>API Settings</b> and restore the last saved values. Deposits are
          applied immediately and will not be reverted.
        </>
      ),
      confirmText: 'Reset',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    resetEditableSections();
    systemAlert.info('Changes have been reverted.');
  }, [confirm, resetEditableSections, systemAlert]);

  const confirmSave = useCallback(async () => {
    const ok = await confirm({
      priority: 'info',
      title: 'Save changes?',
      description: (
        <>
          This will save all changes made to <b>Account Information</b> and{' '}
          <b>API Settings</b>.
        </>
      ),
      confirmText: 'Save',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;
    await handleSave();
  }, [confirm, handleSave]);

  const confirmDeposit = useCallback(async () => {
    if (deposit <= 0) {
      systemAlert.info('Enter a deposit amount greater than 0.');
      return;
    }

    const ok = await confirm({
      priority: 'warning',
      title: 'Apply deposit?',
      description: (
        <>
          You are about to apply a deposit of <b>{toUsdDisplay(deposit)}</b>.
          <br />
          This action is applied immediately.
        </>
      ),
      confirmText: 'Apply',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;
    await applyDeposit();
  }, [applyDeposit, confirm, deposit, systemAlert]);

  /* ------------------------------------------------------------------ */
  /* Section wrapper adapter                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Adapter passed into child cards so they can render inside the same wrapper.
   * The wrapper owns the "highlighted" visuals and spacing rules.
   */
  const sectionWrapper = useCallback(
    (args: SectionWrapperArgs) => (
      <SectionCard {...args} highlighted={args.highlighted} />
    ),
    [],
  );

  /* ------------------------------------------------------------------ */
  /* Page header slots                                                   */
  /* ------------------------------------------------------------------ */

  const leadingTitle = useMemo(() => form.name || 'Partner', [form.name]);

  const leadingDescription = useMemo(() => {
    const id = toDisplayPartnerId(form.partnerId);
    const manual = form.manualOrderEnabled ? 'Enabled' : 'Disabled';
    return `Partner ID: ${id} • Manual Order: ${manual}`;
  }, [form.manualOrderEnabled, form.partnerId]);

  useEffect(() => {
    setPageHeaderSlots({
      leading: {
        title: leadingTitle,
        description: leadingDescription,
      },
      actions: [
        {
          key: 'partner-reset',
          label: 'Reset',
          startIcon: <Replay />,
          variant: 'outlined',
          onClick: confirmReset,
          disabled: !isDirty || saveLoading,
        },
        {
          key: 'partner-save',
          label: saveLoading ? 'Saving…' : 'Save',
          startIcon: <Save />,
          variant: 'contained',
          color: 'primary',
          primary: true,
          onClick: confirmSave,
          disabled: !isDirty || saveLoading,
        },
      ],
    });
  }, [
    confirmReset,
    confirmSave,
    isDirty,
    leadingDescription,
    leadingTitle,
    saveLoading,
    setPageHeaderSlots,
  ]);

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <Box width="100%" sx={{ minWidth: 0 }}>
      <Box mb={2}>
        <AccountInformationCard
          form={form}
          highlighted={isAccountDirty}
          onChangeInput={handleChangeInput}
          onRegeneratePassword={regeneratePassword}
          sectionWrapper={sectionWrapper}
        />
      </Box>

      <Box mb={2}>
        <ApiSettingsCard
          form={form}
          highlighted={isApiDirty}
          onChangeInput={handleChangeInput}
          onRegenerateAppId={regenerateAppId}
          onRegenerateAppSecret={regenerateAppSecret}
          sectionWrapper={sectionWrapper}
        />
      </Box>

      <Box mb={2}>
        <ProductAccessPricingCard
          onManagePricing={() => navigate(`/partners/${safePartnerId}/prices`)}
          sectionWrapper={sectionWrapper}
        />
      </Box>

      <Box mb={2}>
        <BalanceCard
          balance={form.balance}
          deposit={deposit}
          setDeposit={setDeposit}
          onApplyDeposit={confirmDeposit}
          applying={depositApplying}
          sectionWrapper={sectionWrapper}
        />
      </Box>

      <RecentTransactionsTable
        ref={txAnchorRef}
        transactions={transactions}
        isLoading={isTxLoading}
        page={page}
        totalCount={txCount ?? 0}
        pageSize={PAGE_SIZE}
        onChangePage={(_e, value) => setPage(value)}
      />
    </Box>
  );
};

export default PartnerDetail;
